import AggregatedReportsAPI from '../../api/AggregatedReportsApi';
import { GoalRow, FunnelRow, NetworkRow, NetworkGoalDef, NetworkGoalMetric, LifecycleData, LifecycleCohortRow, LifecycleGoalDef, ALL_METRICS, GRADE_COLUMNS, GRADE_LABEL } from './types';
import { getMetricCell, metricDisplayLabel } from './shared';
import { trafficCell, goalCell } from './AdNetworkTab';
import { TRAFFIC_METRICS, TRAFFIC_FUNNEL_METRICS } from './SettingsDrawer';
import { formatAdvancedCell } from './AdvancedFilterTables';
import type { AdvancedFilterTablesData, AdvancedFilterRow } from './AdvancedFilterTables';

// Goal columns of the Traffic Source / Tracking Tag / Custom Tag tables, in the on-screen order.
const ADVANCED_GOAL_METRICS: { suffix: string; label: string }[] = [
  { suffix: '_visiopt_conversions', label: 'Conversions' },
  { suffix: '_visiopt_revenue', label: 'Revenue' },
  { suffix: '_ucr', label: 'UCR' },
  { suffix: '_cr', label: 'CR' },
  { suffix: '_aov', label: 'AOV' },
  { suffix: '_epc', label: 'EPC' },
];

// Same CSV shape the campaign report export writes: section title, header row, data rows,
// blank line between sections.
const csvCell = (v: any) => {
  const s = v === null || v === undefined ? '' : String(v);
  // Ad/ad set ids are long digit strings; ="..." stops Excel turning them into 6.28047E+11.
  if (/^\d{11,}$/.test(s)) { return `="${s}"`; }
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const csvLine = (cells: any[]) => cells.map(csvCell).join(',');

const download = (rows: string[], fileName: string) => {
  const blob = new Blob(['﻿' + rows.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', fileName);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// reportName_Tab_DateRange, sanitised so the file stays identifiable across several exports.
const buildFileName = (reportName: string, tabLabel: string, dateLabel: string) => {
  const clean = (v: string) => String(v || '').trim().replace(/[^a-zA-Z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  return [clean(reportName) || 'Aggregated-Report', clean(tabLabel), clean(dateLabel)]
    .filter((p) => p !== '')
    .join('_') + '.csv';
};

const readJson = (response: any) => (response.ok ? response.json() : null);

// Every column each table can render. The export ignores the Visible Metrics selection so
// the file always carries the full set, whatever the user has switched on.
const GOAL_BREAKDOWN_HIDDEN = ['spend', 'aov', 'profit', 'roi', 'uniques', 'visits', 'ctr', 'cpm'];
const GOAL_BREAKDOWN_ORDER = ALL_METRICS.filter((m) => !GOAL_BREAKDOWN_HIDDEN.includes(m.toLowerCase()));
// Result always travels with Values Recorded, as on screen.
const NETWORK_GOAL_METRICS = TRAFFIC_FUNNEL_METRICS.flatMap((m) => (m === 'Avg. Value' ? [m, 'Scored'] : [m]));

// Result carries the average and/or the word split, so the file keeps both.
const goalExportValue = (gm: NetworkGoalMetric | undefined, m: string) => {
  if (m !== 'Avg. Value') { return goalCell(gm, m).value; }
  const avg = gm?.avg_value && gm.avg_value !== '-' ? gm.avg_value : '';
  const labels = (gm?.value_breakdown ?? []).map((l) => `${l.label} ${Number(l.pct).toFixed(2)}%`);
  return [avg, ...labels].filter((v) => v !== '').join('; ') || '-';
};

// Lifecycle cell formatting mirrors AggregatedLifecycleBody, minus locale thousand
// separators so Excel reads the figures as numbers wherever it can.
const lcMoney = (v: any) => {
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? `$${n.toFixed(2)}` : '$0.00';
};
const lcNumber = (v: any) => {
  if (v === null || v === undefined || v === '') { return '0'; }
  const n = Number(v);
  return Number.isFinite(n) ? String(n) : String(v);
};
const lcPct = (v: any) => (typeof v === 'number' ? `${v.toFixed(2)}%` : '-');

// The lifecycle table as shown on the Total tab: daily cohorts, cohort summaries and the
// weekday/weekend grouping all travel in the file, whichever grouping was on screen.
const pushLifecycleSections = (rows: string[], lifecycle: LifecycleData) => {
  const goals: [string, LifecycleGoalDef][] = Object.entries(lifecycle.campaign_goals ?? {});

  const header = ['Entry Date', 'Total Spend', 'Uniques'];
  goals.forEach(([, g], gi) => {
    header.push(`${g.name} - Conversions`);
    if (gi > 0) { header.push(`${g.name} - % of Previous`); }
    header.push(`${g.name} - Conversion %`, `${g.name} - Cost Per Goal`, `${g.name} - Avg Days`);
  });
  header.push('Revenue per Entry (LTV)', 'ROAS');

  const cohortLine = (label: string, data: LifecycleCohortRow) => {
    const cells: any[] = [label, lcMoney(data.spend), lcNumber(data.uniques)];
    goals.forEach(([goalKey], gi) => {
      const cpGoal = data[`${goalKey}_cp_goal`];
      const avgDays = data[`${goalKey}_avg_days`];
      cells.push(lcNumber(data[`${goalKey}_conversion`]));
      if (gi > 0) { cells.push(lcPct(data[`${goalKey}_perc_prev`])); }
      cells.push(lcPct(data[`${goalKey}_conversion_perc`]));
      cells.push(typeof cpGoal === 'number' ? `$${cpGoal.toFixed(2)}` : '-');
      cells.push(typeof avgDays === 'number' ? avgDays.toFixed(2) : '-');
    });
    cells.push(typeof data.revenue_per_entry === 'number' ? data.revenue_per_entry.toFixed(2) : '-');
    cells.push(typeof data.roas === 'number' ? `${data.roas.toFixed(2)}x` : '-');
    return csvLine(cells);
  };

  const sections: [string, Record<string, LifecycleCohortRow>][] = [
    ['Lifecycle - Daily Cohorts', lifecycle.daily_report_data ?? {}],
    ['Lifecycle - Cohort Summaries', lifecycle.summaries ?? {}],
    ['Lifecycle - Weekday vs Weekend', lifecycle.weekday_weekend_summaries ?? {}],
  ];
  sections.forEach(([title, cohorts]) => {
    const entries = Object.entries(cohorts);
    if (entries.length === 0) { return; }
    if (rows.length > 0 && rows[rows.length - 1] !== '') { rows.push(''); }
    rows.push(title);
    rows.push(csvLine(header));
    entries.forEach(([key, data]) => rows.push(cohortLine(data.summary_key ?? key, data)));
  });
};

// Grade cells are rendered by GradeCellTd, not the metric resolvers, so read them directly.
const gradeValue = (row: any, metric: string) =>
  metric === 'Grade'
    ? (row.grade ? (GRADE_LABEL as any)[row.grade] ?? '-' : '-')
    : (row.grade_reason ?? '-');

export type AggregateExportOptions = {
  context: string;
  reportName: string;
  baseParams: Record<string, any>;
  // The Total tab's Traffic Source / Tracking Tag / Custom Tag tables as shown, so the file matches the screen.
  advancedTables?: AdvancedFilterTablesData;
};

// Builds and downloads the CSV for the active tab. Every request reuses the report's own
// params, so filters, attribution, date range and visible columns match what is on screen.
export async function exportAggregateReport(opts: AggregateExportOptions): Promise<string> {
  const { context, reportName, baseParams } = opts;

  if (context === 'Total Settings') {
    const [metricsRes, goalsRes, lifecycleRes] = await Promise.all([
      AggregatedReportsAPI.getprimarygoalmetrics(baseParams).then(readJson),
      AggregatedReportsAPI.getgoalbreakdown(baseParams).then(readJson),
      // A lifecycle failure never blocks the export; the file just ships without that section.
      AggregatedReportsAPI.getlifecycle(baseParams).then(readJson).catch(() => null),
    ]);
    const metrics = metricsRes?.data?.metrics ?? null;
    const dateLabel = metricsRes?.data?.date_range_label ?? '';
    const goalRows: GoalRow[] = Array.isArray(goalsRes?.data?.goal_rows) ? goalsRes.data.goal_rows : [];
    const lifecycle: LifecycleData | null =
      lifecycleRes?.status === 1 && lifecycleRes?.data?.eligible ? lifecycleRes.data : null;
    if (!metrics && goalRows.length === 0) { return ''; }

    const rows: string[] = [];
    if (metrics) {
      rows.push('Total');
      rows.push(csvLine(ALL_METRICS));
      rows.push(csvLine(ALL_METRICS.map((m) => getMetricCell(metrics, m).value)));
      rows.push('');
    }
    if (goalRows.length > 0) {
      rows.push('Goal Breakdown');
      rows.push(csvLine(['Goal', ...GOAL_BREAKDOWN_ORDER]));
      goalRows.forEach((r) => {
        rows.push(csvLine([r.goal, ...GOAL_BREAKDOWN_ORDER.map((m) => getMetricCell(r, m).value)]));
        // Device / Hour / Source segments follow their goal, as on screen.
        (r.device_breakdown?.length ? r.device_breakdown : r.hour_breakdown?.length ? r.hour_breakdown : r.source_breakdown ?? []).forEach((seg) => {
          rows.push(csvLine([`${r.goal} - ${seg.goal}`, ...GOAL_BREAKDOWN_ORDER.map((m) => getMetricCell(seg, m).value)]));
        });
      });
    }
    const adv = opts.advancedTables;
    if (adv && adv.eligible) {
      const sections: [string, AdvancedFilterRow[]][] = [
        ['Traffic Source', adv.source],
        ['Tracking Tags', adv.tracking_tag],
        ...Object.entries(adv.custom_tag || {}).map(([tag, list]) => [`Custom Tag - ${tag}`, list] as [string, AdvancedFilterRow[]]),
      ];
      const goalPrefixes = adv.goals.map((g) => g.column_key);
      sections.forEach(([title, list]) => {
        if (!Array.isArray(list) || list.length === 0) { return; }
        // Identifier columns are every non-metric key, as the on-screen table picks them.
        const idKeys: string[] = [];
        list.forEach((row) => Object.keys(row).forEach((k) => {
          if (idKeys.includes(k) || k === 'uniques' || k === 'visits' || k.endsWith('_id') || goalPrefixes.some((p) => k.startsWith(p + '_'))) { return; }
          idKeys.push(k);
        }));
        rows.push('');
        rows.push(title);
        rows.push(csvLine([...idKeys, 'Uniques', 'Visits', ...adv.goals.flatMap((g) => ADVANCED_GOAL_METRICS.map((m) => `${g.display_name} - ${m.label}`))]));
        list.forEach((row) => {
          rows.push(csvLine([
            ...idKeys.map((k) => (row[k] === null || row[k] === undefined || row[k] === '' ? '-' : String(row[k]))),
            String(row.uniques ?? 0),
            String(row.visits ?? 0),
            ...adv.goals.flatMap((g) => ADVANCED_GOAL_METRICS.map((m) => formatAdvancedCell(
              row[g.column_key + m.suffix], m.suffix, parseInt(String(row[g.column_key + '_visiopt_conversions'] ?? 0), 10) || 0,
            ))),
          ]));
        });
      });
    }
    if (lifecycle) { pushLifecycleSections(rows, lifecycle); }
    const name = buildFileName(reportName, 'Total', dateLabel);
    download(rows, name);
    return name;
  }

  if (context === 'Funnel Settings') {
    const res = await AggregatedReportsAPI.getfunnelrows(baseParams).then(readJson);
    const funnelRows: FunnelRow[] = Array.isArray(res?.data?.funnel_rows) ? res.data.funnel_rows : [];
    if (funnelRows.length === 0) { return ''; }

    const rows: string[] = [];
    rows.push('Funnel (Visiopt Campaign)');
    rows.push(csvLine(['Visiopt Campaign', ...ALL_METRICS]));
    funnelRows.forEach((r) => {
      rows.push(csvLine([r.name, ...ALL_METRICS.map((m) => getMetricCell(r, m).value)]));
    });
    const name = buildFileName(reportName, 'Funnel', res?.data?.date_range_label ?? '');
    download(rows, name);
    return name;
  }

  // Campaign / Ad Set / Ad all produce the same file: the full breakdown at every level, so
  // exporting from any of the three never yields a partial file.
  const levels: { level: string; label: string }[] = [
    { level: 'campaign', label: 'Campaign (Ad Network Campaign)' },
    { level: 'adset', label: 'Ad Set' },
    { level: 'ad', label: 'Ad' },
  ];
  const payloads = await Promise.all(
    levels.map((l) =>
      AggregatedReportsAPI
        .getadnetworkdata({ ...baseParams, level: l.level, page: 1, export: 1 })
        .then(readJson)
    )
  );

  const rows: string[] = [];
  let dateLabel = '';
  payloads.forEach((res, i) => {
    const data = res?.data;
    if (!data) { return; }
    if (dateLabel === '') { dateLabel = data.date_range_label ?? ''; }
    // Same order as on screen: ad rows first, then Organic, then Direct.
    const allRows: NetworkRow[] = Array.isArray(data.rows) ? data.rows : [];
    const netRows: NetworkRow[] = [
      ...allRows.filter((r) => r.source !== 'organic' && r.source !== 'direct'),
      ...allRows.filter((r) => r.source === 'organic'),
      ...allRows.filter((r) => r.source === 'direct'),
    ];
    // Every goal the report knows about, with every per-goal metric.
    const activeGoals: NetworkGoalDef[] = Array.isArray(data.goals) ? data.goals : [];

    // Identifier columns follow the level, so an Ad row still names its campaign and ad set.
    const hasGoogle = netRows.find((e) => e.source === 'google');
    let idCols: string[];
    if(hasGoogle){
      idCols = levels[i].level === 'campaign'
        ? ['Source', 'Campaign', 'Placement']
        : levels[i].level === 'adset'
        ? ['Source', 'Campaign', 'Ad Set', 'Placement']
        : ['Source', 'Campaign', 'Ad Set', 'Ad', 'Placement'];
    }else{
      idCols = levels[i].level === 'campaign'
        ? ['Source', 'Campaign']
        : levels[i].level === 'adset'
        ? ['Source', 'Campaign', 'Ad Set']
        : ['Source', 'Campaign', 'Ad Set', 'Ad'];
    }

    const header = [...idCols, ...TRAFFIC_METRICS];
    activeGoals.forEach((g) => {
      NETWORK_GOAL_METRICS.forEach((m) => header.push(`${g.name} - ${metricDisplayLabel(m)}`));
    });

    rows.push(levels[i].label);
    rows.push(csvLine(header));
    if (netRows.length === 0) {
      rows.push(csvLine(['No data']));
    } else {
      netRows.forEach((r) => {
        // ChatGPT, Organic and Direct have no campaign name; the screen shows "-".
        const isAggregateRow = r.source === 'chatgpt' || r.source === 'organic' || r.source === 'direct';
        const ids = levels[i].level === 'campaign'
          ? [r.source_label, isAggregateRow ? '-' : (r.campaign ?? r.entity_name)]
          : levels[i].level === 'adset'
          ? [r.source_label, r.campaign ?? '', r.adset ?? r.entity_name]
          : [r.source_label, r.campaign ?? '', r.adset ?? '', r.ad ?? r.entity_name];
        // Placement is only a column when a Google row is present; otherwise every metric shifts.
        if (hasGoogle) { ids.push(r.placement && r.placement !== '' ? r.placement : '-'); }
        const line = [
          ...ids,
          ...TRAFFIC_METRICS.map((m) => (GRADE_COLUMNS.includes(m) ? gradeValue(r, m) : trafficCell(r, m).value)),
        ];
        activeGoals.forEach((g) => {
          NETWORK_GOAL_METRICS.forEach((m) => line.push(goalExportValue(r.goals?.[g.key], m)));
        });
        rows.push(csvLine(line));

        // Device / hour / customer-type sub-rows, indented the way the campaign report exports them.
        const subRows = (r.device_breakdown && r.device_breakdown.length > 0)
          ? r.device_breakdown
          : (r.hour_breakdown && r.hour_breakdown.length > 0)
          ? r.hour_breakdown
          : (r.customer_type_breakdown ?? []);
        const isCt = Array.isArray(r.customer_type_breakdown) && r.customer_type_breakdown.length > 0
          && !(r.device_breakdown && r.device_breakdown.length > 0)
          && !(r.hour_breakdown && r.hour_breakdown.length > 0);
        // Customer Type only splits eligible purchase goals; others export "-" so an empty
        // cell is never read as a zero.
        const ctEligible = (colKey: string) => {
          const flags = r.customer_type_eligible;
          if (!flags || typeof flags !== 'object') { return false; }
          const hit = Object.keys(flags).find((k) => colKey.includes(k) || k.includes(colKey));
          return hit ? Number(flags[hit]) === 1 : false;
        };
        subRows.forEach((sub: any) => {
          const label = sub.device_name ?? sub.hour_label ?? sub.customer_type_name ?? '';
          const subLine: any[] = ids.map((_, ci) => (ci === ids.length - 1 ? `  -> ${label}` : ''));
          TRAFFIC_METRICS.forEach((m) => subLine.push(
            isCt ? '-' : (GRADE_COLUMNS.includes(m) ? gradeValue(sub, m) : trafficCell(sub, m).value)
          ));
          activeGoals.forEach((g) => {
            NETWORK_GOAL_METRICS.forEach((m) => {
              subLine.push(isCt && !ctEligible(g.key) ? '-' : goalExportValue(sub.goals?.[g.key], m));
            });
          });
          rows.push(csvLine(subLine));
        });
      });
    }
    rows.push('');
  });

  if (rows.length === 0) { return ''; }
  const name = buildFileName(reportName, 'Ad-Network', dateLabel);
  download(rows, name);
  return name;
}

export async function exportAggregateKeywords(opts: { reportName: string; baseParams: Record<string, any> }): Promise<string> {
  const res = await AggregatedReportsAPI.getkeywordexport(opts.baseParams).then(readJson);
  const data = res?.data;
  const adsetRows: any[] = Array.isArray(data?.adset_keywords) ? data.adset_keywords : [];
  const adRows: any[] = Array.isArray(data?.ad_keywords) ? data.ad_keywords : [];
  if (adsetRows.length === 0 && adRows.length === 0) { return ''; }
  const goals: { key: string; name: string }[] = Array.isArray(data?.goals) ? data.goals : [];
  const keywordLabel = (k: any) => (k && k !== '-' ? k : 'No keyword');

  const rows: string[] = [];
  rows.push('Ad Set Keywords (Google)');
  rows.push(csvLine(['Campaign', 'Ad Set', 'Ad', 'Keyword', 'Match Type', 'Impressions', 'Clicks', 'Spend']));
  if (data?.google_unavailable === 1) {
    rows.push(csvLine(['Google did not return keyword data for some ad sets; they are missing below.']));
  }
  if (adsetRows.length === 0) {
    rows.push(csvLine(['No data']));
  } else {
    // "All ads": Google's figures cover the whole ad set, so they are never divided per ad.
    adsetRows.forEach((r) => rows.push(csvLine([
      r.campaign, r.adset, 'All ads', keywordLabel(r.keyword), r.match_type || '-', r.impressions, r.clicks, Number(r.spend || 0).toFixed(2),
    ])));
  }
  rows.push('');

  rows.push('Ad Keywords (Visiopt)');
  const header = ['Campaign', 'Ad Set', 'Ad', 'Keyword', 'Uniques', 'Visits'];
  goals.forEach((g) => header.push(`${g.name} - Conversions`, `${g.name} - UCR`, `${g.name} - CR`, `${g.name} - Revenue`));
  rows.push(csvLine(header));
  if (adRows.length === 0) {
    rows.push(csvLine(['No data']));
  } else {
    adRows.forEach((r) => {
      const line: any[] = [r.campaign, r.adset, r.ad, keywordLabel(r.keyword), r.uniques, r.visits];
      goals.forEach((g) => {
        const m = r.goals?.[g.key] ?? {};
        line.push(m.conversions ?? 0, `${m.ucr ?? '0.00'}%`, `${m.cr ?? '0.00'}%`, Number(m.revenue ?? 0).toFixed(2));
      });
      rows.push(csvLine(line));
    });
  }

  const name = buildFileName(opts.reportName, 'Keywords', data?.date_range_label ?? '');
  download(rows, name);
  return name;
}
